import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import ManualCompose from "@/pages/ManualCompose";
import {
  TopBar, MetaCard, ManuscriptCard, AiToolsCard, ChapterList, ChapterPreview,
  ParserWarningsBanner,
} from "@/pages/BookEditorParts";
import { BookKitPreview } from "@/components/manual/BookKitPreview";
import { StructurePanel } from "@/components/manual/StructurePanel";
import { ChapterHeroPanel } from "@/components/manual/ChapterHeroPanel";
import { ChapterHeroBatchModal } from "@/components/manual/ChapterHeroBatchModal";
import { BookMediaLibrary } from "@/components/manual/BookMediaLibrary";
import { PublishReadyCard } from "@/components/BillingWidgets";
import { RefreshCw } from "lucide-react";
import { EditHistoryPanel } from "@/components/manual/EditHistoryPanel";
import { useEditHistory } from "@/components/manual/useEditHistory";
import { BookTagsBar } from "@/components/manual/BookTagsBar";
import { BookLockBar } from "@/components/manual/BookLockBar";
import { BookPresenceBar, useBookPresence } from "@/components/manual/BookPresenceBar";
import { PasteStyleToggle } from "@/components/manual/PasteStyleToggle";
import { WorkspacePickerButton } from "@/pages/Dashboard";
import { useAuth } from "@/context/AuthContext";

// -----------------------------------------------------------------------------
// Data hooks — extracted so the parent component stays focused on layout.
// -----------------------------------------------------------------------------

function useBook(id, nav) {
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/books/${id}`);
      setBook(data);
    } catch (e) {
      toast.error(formatApiError(e));
      nav("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  const patch = useCallback(async (updates) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/books/${id}`, updates);
      setBook(data);
      return data;
    } catch (e) {
      // iter-107 — 409 stale_tab_wipe is a data-safety refusal from
      // the backend guard. Surface it as a persistent, actionable
      // toast so the author knows to reload before saving again
      // (a normal toast.error would auto-dismiss and the auto-save
      // would fire the same wipe every 15s).
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : undefined;
      if (e?.response?.status === 409 && code === "stale_tab_wipe") {
        toast.error(detail?.message || "Save blocked to protect your book.", {
          duration: 20000,
          action: {
            label: "Reload page",
            onClick: () => window.location.reload(),
          },
        });
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSaving(false);
    }
  }, [id]);

  return { book, setBook, loading, saving, patch };
}

function useUploads(id, setBook, setActiveIdx, onUploaded) {
  const uploadManuscript = useCallback(async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/books/${id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBook(data);
      // iter-33: auto-advance past Part dividers so the first thing the
      // user sees after upload is a real chapter card with the hero-image
      // panel visible. Selecting index 0 landed on a "PART I" divider for
      // books with Parts, which hid the ChapterHeroPanel entirely and
      // made users report "no hero image generator visible" (Aug 2026).
      const chapters = data.chapters || [];
      const firstChapterIdx = chapters.findIndex((c) => c && c._kind === "chapter");
      setActiveIdx(firstChapterIdx >= 0 ? firstChapterIdx : 0);
      toast.success(`Detected ${data.chapters.length} chapter(s)`);
      // iter-34: caller may want to kick off batch hero generation right
      // after the DOCX lands (opt-in via the "Also generate hero images"
      // checkbox on the ManuscriptCard).
      onUploaded?.(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [id, setBook, setActiveIdx, onUploaded]);

  const uploadCover = useCallback(async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/books/${id}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBook(data);
      toast.success("Cover updated");
    } catch (e) { toast.error(formatApiError(e)); }
  }, [id, setBook]);

  return { uploadManuscript, uploadCover };
}

function useAi(id, book, setBook, activeIdx) {
  const [aiBusy, setAiBusy] = useState(null);

  const aiDetect = useCallback(async () => {
    setAiBusy("detect");
    try {
      const { data } = await api.post(`/books/${id}/ai/detect-chapters`);
      setBook(data);
      toast.success(`AI split into ${data.chapters.length} chapter(s)`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiBusy(null); }
  }, [id, setBook]);

  const aiBlurb = useCallback(async () => {
    setAiBusy("blurb");
    try {
      const { data } = await api.post(`/books/${id}/ai/blurb`);
      setBook((prev) => ({ ...prev, description: data.description }));
      toast.success("Blurb generated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiBusy(null); }
  }, [id, setBook]);

  const aiFixTypos = useCallback(async () => {
    if (!book?.chapters?.length) return;
    setAiBusy("typos");
    try {
      const { data } = await api.post(`/books/${id}/ai/fix-typos`, null, {
        params: { chapter_index: activeIdx },
      });
      setBook((prev) => {
        const chapters = [...(prev?.chapters || [])];
        chapters[activeIdx] = { ...chapters[activeIdx], content: data.content };
        return { ...prev, chapters };
      });
      toast.success("Typos fixed for this chapter");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiBusy(null); }
  }, [id, book, setBook, activeIdx]);

  return { aiBusy, aiDetect, aiBlurb, aiFixTypos };
}

async function downloadFile(id, kind, title, opts = {}) {
  // iter-107 — Direct navigation download. The previous
  // fetch→blob→click approach broke for any book whose export took
  // more than ~5 seconds to build: Chrome / Safari expire the
  // "user gesture" that authorized the programmatic click by the
  // time the fetch resolves, and silently swallow the download.
  // The toast still fired because the fetch resolved fine — hence
  // the "PDF downloaded but nothing is downloaded" bug report.
  //
  // Using a real <a href="…"> click on the export URL avoids this:
  //   * the click is happening on a synchronous user gesture, so
  //     the browser trusts it forever;
  //   * cookie auth attaches to the request automatically;
  //   * `Content-Disposition: attachment` on the response tells
  //     the browser to save the file rather than navigate to it;
  //   * we get a native browser download progress bar instead of a
  //     silent 12-second wait with no feedback.
  try {
    const qs = kind === "pdf" && opts.stylePack ? `?style_pack=${encodeURIComponent(opts.stylePack)}` : "";
    const url = `${API}/books/${id}/export/${kind}${qs}`;
    // Best-effort filename sanitisation (browsers already strip
    // slashes / control chars, but colons trip some Windows shells).
    const safe = (title || "book").replace(/[\\/:*?"<>|]/g, " ").trim() || "book";
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safe}.${kind}`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(`${kind.toUpperCase()} download started`);
  } catch (e) {
    toast.error(e.message || "Export failed");
  }
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export default function BookEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const [activeIdx, setActiveIdx] = useState(0);
  const [showManualCompose, setShowManualCompose] = useState(false);
  // iter-34: auto-hero-generation-on-upload opt-in. When the checkbox
  // on the ManuscriptCard is on and the DOCX finishes importing, we
  // open the batch modal at ``/book/:id`` so the user can review the
  // shared prompt and either accept or bail before Nano Banana runs.
  const [autoGenerateHeroes, setAutoGenerateHeroes] = useState(false);
  const [autoBatchBook, setAutoBatchBook] = useState(null);
  // Ref keeps the callback below free of the checkbox value as a
  // dependency, so ``useUploads`` doesn't rebuild ``uploadManuscript``
  // every render just because the checkbox toggled.
  const autoGenerateHeroesRef = useRef(false);
  useEffect(() => { autoGenerateHeroesRef.current = autoGenerateHeroes; },
             [autoGenerateHeroes]);
  const [stylePack, setStylePack] = useState("classic_trade");
  const fileRef = useRef(null);

  const { book, setBook, loading, saving, patch } = useBook(id, nav);
  // iter-47: presence heartbeat for shared workspace books. Solo books
  // no-op the hook (isShared=false) so no traffic is generated.
  const presence = useBookPresence(id, !!book?.workspace_id);
  // iter-39: durable last-20 timeline of book edits with one-click revert.
  const history = useEditHistory(id, (fresh) => { if (fresh) setBook(fresh); });
  // iter-34: after DOCX upload, if the user opted in AND at least one
  // real chapter is missing a hero image, open the batch modal
  // pre-filled with the saved shared prompt template so they can
  // review/edit the prompt before Nano Banana is invoked. Matches the
  // checkbox copy on ManuscriptCard: "You can edit the shared prompt
  // template before it starts."
  const handleUploaded = useCallback((freshBook) => {
    if (!autoGenerateHeroesRef.current) return;
    const missing = (freshBook?.chapters || []).some(
      (c) => c && c._kind === "chapter"
              && typeof c.chapter_source_index === "number"
              && !c.hero_image);
    if (!missing) {
      toast.info("Every chapter already has a hero image — skipping auto-generation.");
      return;
    }
    setAutoBatchBook(freshBook);
  }, []);
  const { uploadManuscript, uploadCover } = useUploads(id, setBook, setActiveIdx, handleUploaded);
  const { aiBusy, aiDetect, aiBlurb, aiFixTypos } = useAi(id, book, setBook, activeIdx);

  // iter-33: whenever the book loads/reloads, make sure the initially
  // selected chapter card is a real chapter (not a Part divider), so
  // the ChapterHeroPanel renders on landing. Only fires when the
  // currently active index is out of bounds or points at a Part —
  // manual chapter picks by the user are respected.
  useEffect(() => {
    if (!book?.chapters?.length) return;
    const cur = book.chapters[activeIdx];
    const isPartOrMissing = !cur || cur._kind === "part";
    if (!isPartOrMissing) return;
    const firstChapterIdx = book.chapters.findIndex((c) => c && c._kind === "chapter");
    if (firstChapterIdx >= 0 && firstChapterIdx !== activeIdx) {
      setActiveIdx(firstChapterIdx);
    }
  }, [book, activeIdx]);

  // iter-66 — hooks must be called before any early return.
  const [refreshing, setRefreshing] = useState(false);

  // iter-108 — Admin-only cross-workspace assignment. Loads every
  // workspace so a super-admin can drop this book into any team's
  // library as a reference/sample. The picker calls PATCH /books/:id
  // with the plural `workspace_ids` array (single source of truth,
  // live-syncs across every workspace it's shared with).
  const [allWorkspaces, setAllWorkspaces] = useState([]);
  const [wsSaving, setWsSaving] = useState(false);
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/workspaces/all");
        setAllWorkspaces(data || []);
      } catch { /* non-fatal */ }
    })();
  }, [isAdmin]);
  const saveAdminWorkspaces = async (wsIds) => {
    setWsSaving(true);
    try {
      const { data } = await api.patch(`/books/${id}`, { workspace_ids: wsIds });
      setBook(data);
      toast.success(wsIds.length
        ? `Book now appears in ${wsIds.length} workspace${wsIds.length === 1 ? "" : "s"} · live-synced`
        : "Removed from every workspace");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setWsSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Nav />
        <div className="max-w-[1400px] mx-auto px-6 py-16 text-[color:var(--text-muted)]">Loading book…</div>
      </div>
    );
  }
  if (!book) return null;

  const activeChapter = book.chapters?.[activeIdx];
  const onExport = (kind) => downloadFile(id, kind, book.title, { stylePack });

  // iter-66 — build a Set of locked source_indices from book state so
  // the chapter sidebar can render padlock icons + a per-chapter
  // Lock/Unlock toggle.
  const lockedIndices = new Set(
    Object.keys(book.formatter_v2_locked_blocks || {}).map((k) => Number(k))
  );
  const toggleChapterLock = async (sourceIndex, nextLocked) => {
    if (sourceIndex == null) return;
    // Optimistic — mirror the server change locally so the icon flips instantly.
    setBook((prev) => {
      const next = { ...(prev.formatter_v2_locked_blocks || {}) };
      if (nextLocked) next[String(sourceIndex)] = true;
      else delete next[String(sourceIndex)];
      return { ...prev, formatter_v2_locked_blocks: next };
    });
    try {
      await api.patch(`/books/${id}/structure/${sourceIndex}/lock`,
                          { locked: !!nextLocked });
      toast.success(nextLocked ? "Chapter locked" : "Chapter unlocked");
    } catch (err) {
      toast.error(formatApiError(err));
      // Rollback.
      setBook((prev) => {
        const next = { ...(prev.formatter_v2_locked_blocks || {}) };
        if (nextLocked) delete next[String(sourceIndex)];
        else next[String(sourceIndex)] = true;
        return { ...prev, formatter_v2_locked_blocks: next };
      });
    }
  };

  // iter-66 — Refresh Formatter: re-runs the classification + rendering
  // pipeline against this book's persisted source blocks so earlier
  // uploads benefit from every engine upgrade (auto-bullets, ad-hoc H3,
  // named callouts, callout color variants, auto-lock, spelled-out
  // chapter numbers, etc.) without needing a re-upload.
  const refreshFormatter = async () => {
    if (refreshing) return;
    if (!window.confirm(
      "Re-run the current formatter pipeline against this book?\n\n"
      + "This picks up all engine upgrades shipped since your original upload "
      + "(callouts, auto-bullets, section detection, and auto-lock). "
      + "Your manual role overrides + locks are preserved."
    )) return;
    setRefreshing(true);
    try {
      const { data } = await api.post(`/books/${id}/formatter/reprocess`);
      setBook(data.book);
      toast.success(`Reprocessed ${data.reclassified_blocks} blocks · ${data.locked_blocks} locked`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRefreshing(false);
    }
  };

  // Chapter list mutations — optimistic local update + PATCH the whole
  // chapters[] to /books/:id (the existing patch() helper serialises the
  // save through the parent state). Adjust activeIdx to follow the moved
  // chapter so the preview pane keeps showing the same content.
  const _persistChapters = (nextChapters) => {
    presence.markTyping();
    setBook((prev) => ({ ...prev, chapters: nextChapters }));
    patch({ chapters: nextChapters });
  };
  const reorderChapters = (from, to) => {
    if (!book.chapters || from === to) return;
    const list = [...book.chapters];
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    history.record("chapter_reorder",
      `Moved "${moved?.title || `chapter ${from + 1}`}" from position ${from + 1} to ${to + 1}`);
    _persistChapters(list);
    // Follow the moved chapter if it was the active one.
    setActiveIdx((cur) => {
      if (cur === from) return to;
      if (from < cur && to >= cur) return cur - 1;
      if (from > cur && to <= cur) return cur + 1;
      return cur;
    });
  };
  const renameChapter = (idx, newTitle) => {
    if (!book.chapters?.[idx]) return;
    const prevTitle = book.chapters[idx].title || "(untitled)";
    if (prevTitle === newTitle) return;
    history.record("chapter_rename",
      `Renamed "${prevTitle}" → "${newTitle || "(untitled)"}"`);
    const list = book.chapters.map((c, i) => (i === idx ? { ...c, title: newTitle } : c));
    _persistChapters(list);
  };
  const deleteChapter = (idx) => {
    if (!book.chapters?.[idx]) return;
    const removed = book.chapters[idx];
    history.record("chapter_delete",
      `Deleted "${removed?.title || `chapter ${idx + 1}`}"`);
    const list = book.chapters.filter((_, i) => i !== idx);
    _persistChapters(list);
    setActiveIdx((cur) => Math.max(0, cur >= list.length ? list.length - 1 : (idx < cur ? cur - 1 : cur)));
  };
  // Reclassify a detected chapter into front / back matter. The chapter's
  // title + content become a new custom section (key = `custom_<uid>`)
  // appended to the target section list. Non-canonical keys survive the
  // backend's `merge_sections()` pass (added in iter-38).
  const reclassifyChapter = (idx, target) => {
    const ch = book.chapters?.[idx];
    if (!ch) return;
    const label = target === "front_matter" ? "Front matter" : "Back matter";
    if (!window.confirm(`Move "${ch.title}" into ${label}? This removes it from the chapter list.`)) return;
    history.record("chapter_reclassify",
      `Moved "${ch.title || "chapter"}" into ${label}`);
    const uid = Math.random().toString(36).slice(2, 10);
    const newSection = {
      key: `custom_${uid}`,
      title: ch.title || (target === "front_matter" ? "Front matter" : "Back matter"),
      content: ch.content || "",
      include: true,
    };
    const nextChapters = book.chapters.filter((_, i) => i !== idx);
    const field = target === "front_matter" ? "front_matter_sections" : "back_matter_sections";
    const nextSections = [...(book[field] || []), newSection];
    setBook((prev) => ({ ...prev, chapters: nextChapters, [field]: nextSections }));
    presence.markTyping();
    patch({ chapters: nextChapters, [field]: nextSections });
    setActiveIdx((cur) => Math.max(0, cur >= nextChapters.length ? nextChapters.length - 1 : (idx < cur ? cur - 1 : cur)));
    toast.success(`Moved "${ch.title}" to ${label}`);
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="max-w-[1500px] mx-auto px-6 py-8">
        <TopBar onExport={onExport} stylePack={stylePack} setStylePack={setStylePack}
                report={book.formatter_v2_report} bookId={id} />

        <BookKitPreview book={book} setBook={setBook} stylePack={stylePack} />

        <BookLockBar book={book} onBookUpdated={() => {/* async refresh handled by component */}} />
        <BookPresenceBar book={book} presence={presence} />
        <BookTagsBar bookId={id} />

        <PasteStyleToggle book={book} patch={patch} />

        <PublishReadyCard book={book}/>

        <MetaCard
          book={book} setBook={setBook} patch={patch}
          saving={saving} aiBusy={aiBusy} aiBlurb={aiBlurb}
          uploadCover={uploadCover}
        />

        {isAdmin && allWorkspaces.length > 0 && (
          <div
            className="border border-[color:var(--primary)] bg-[color:var(--canvas)] px-5 py-4 mb-6"
            data-testid="admin-ws-assign-bar"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="uppercase text-[10px] tracking-[0.25em] text-[color:var(--primary)]">
                  § Super-admin · Cross-workspace assignment
                </p>
                <p className="font-editorial text-lg mt-1">Share this book across workspaces</p>
                <p className="text-[11px] text-[color:var(--text-muted)] mt-1 max-w-xl">
                  Tick every workspace this book should live in. Members of each
                  workspace see the same live copy — edits sync in real time. Members
                  of one workspace never see members of another.
                </p>
              </div>
              <div className="w-64">
                <WorkspacePickerButton
                  bookId={id}
                  currentIds={
                    (book.workspace_ids && book.workspace_ids.length)
                      ? book.workspace_ids
                      : (book.workspace_id ? [book.workspace_id] : [])
                  }
                  workspaces={allWorkspaces}
                  disabled={wsSaving}
                  onSave={saveAdminWorkspaces}
                  itemLabel="book"
                />
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-12 gap-6">
          <aside className="col-span-12 lg:col-span-4 xl:col-span-3 space-y-4">
            <ManuscriptCard
              book={book}
              fileRef={fileRef}
              uploadManuscript={uploadManuscript}
              openManualCompose={()=>setShowManualCompose(true)}
              autoGenerateHeroes={autoGenerateHeroes}
              setAutoGenerateHeroes={setAutoGenerateHeroes}
            />
            <ParserWarningsBanner bookId={id} report={book.formatter_v2_report} />
            <AiToolsCard
              book={book} aiBusy={aiBusy}
              aiDetect={aiDetect} aiFixTypos={aiFixTypos}
            />
            <ChapterList
              chapters={book.chapters}
              activeIdx={activeIdx}
              setActiveIdx={setActiveIdx}
              lockedIndices={lockedIndices}
              /* iter-70 — the Contents sidebar is now navigation-only.
                 Every editing action (rename, reorder, delete, lock,
                 reclassify) lives inside the Manual Entry panel so
                 authors have a single, unambiguous place to edit the
                 book structure. Lock state still renders as a read-only
                 badge because it's the fastest way to see confirmed
                 chapters while scanning the sidebar. */
            />
            {/* iter-66 — Refresh Formatter · re-runs the pipeline so
                pre-existing books pick up every engine upgrade. */}
            <button
              type="button"
              onClick={refreshFormatter}
              disabled={refreshing}
              className="w-full mt-2 border border-[color:var(--border)] bg-white px-4 py-2 text-sm flex items-center justify-center gap-2 hover:border-[color:var(--primary)] hover:text-[color:var(--primary)] transition-colors disabled:opacity-50"
              data-testid="book-refresh-formatter"
              title="Re-run the current formatter pipeline against this book — picks up recent engine upgrades (auto-bullets, ad-hoc H3, callouts, callout colors, auto-lock)."
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5}/>
              {refreshing ? "Re-processing…" : "Refresh formatter"}
            </button>
            <EditHistoryPanel history={history} />
          </aside>

          <main className="col-span-12 lg:col-span-8 xl:col-span-9">
            {activeChapter && activeChapter._kind !== "part" && (
              <ChapterHeroPanel
                // key forces the panel to remount when the user switches
                // chapters so its local mode/prompt state resets to the
                // new chapter's values (iter-21 review fix).
                key={activeChapter.chapter_source_index ?? activeIdx}
                book={book}
                chapter={activeChapter}
                onBookUpdated={setBook}
              />
            )}
            {activeChapter && activeChapter._kind !== "part" && (
              <div className="mt-4 border border-[color:var(--border)] bg-white px-5 py-2">
                <p className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)]">
                  § Live preview
                </p>
                <p className="text-[11px] text-[color:var(--text-muted)] mt-1">
                  Read-only render of your book — edits happen in the{" "}
                  <strong>Manual entry</strong> panel above and update here in real time.
                </p>
              </div>
            )}
            <div className="editor-canvas border border-[color:var(--border)] min-h-[70vh] p-6 md:p-10">
              <ChapterPreview
                book={book}
                activeChapter={activeChapter}
                activeIdx={activeIdx}
              />
            </div>
            <StructurePanel bookId={id} onBookUpdated={setBook} history={history} />
            <BookMediaLibrary book={book} onBookUpdated={setBook} />
          </main>
        </div>
      </div>
      {showManualCompose && (
        <ManualCompose
          book={book}
          patch={patch}
          reloadBook={async () => {
            try {
              const { data } = await api.get(`/books/${id}`);
              setBook(data);
            } catch (err) { toast.error(formatApiError(err)); }
          }}
          onReclassifyChapter={reclassifyChapter}
          /* iter-71 — Live preview follows selection. The main preview
             pane already re-renders when activeIdx changes; forwarding
             it here lets Manual Entry scroll the matching card into
             view whenever the author clicks a Contents row on the left. */
          activeIdx={activeIdx}
          onClose={()=>setShowManualCompose(false)}
        />
      )}
      {/* iter-34: opt-in automatic hero-image batch after DOCX upload.
          We always render the *manual* batch modal in idle state so the
          user reviews the shared prompt template before Nano Banana
          runs; closing the modal exits the auto flow without generating. */}
      {autoBatchBook && (
        <ChapterHeroBatchModal
          book={autoBatchBook}
          onClose={() => setAutoBatchBook(null)}
          onBookUpdated={(fresh) => { setBook(fresh); setAutoBatchBook(fresh); }}
        />
      )}
    </div>
  );
}
